import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { fetchAircraftLatest, updateProject, createProject, createSubtasks, updateSubtask, updateCallout, completeAllProjectSubtasks } from '../lib/api'
import AircraftModal from './AircraftModal'
import { logChange } from '../lib/auditLog'
import { DEPT_COLORS } from '../lib/deptColors'
import { addBusinessDays, businessDayDelta, nextBusinessDay } from '../lib/businessDays'
import { generateAutoSubtasks } from '../lib/autoMilestones'
import BulkMilestonePushPanel from './BulkMilestonePushPanel'

const STATUSES = ['TBD', 'Scheduled', 'In Work', 'Complete']
const SCHEDULE_STATUSES = [
  { value: 'ahead',    label: 'Ahead of Schedule' },
  { value: 'tracking', label: 'On Track' },
  { value: 'at_risk',  label: 'At Risk' },
  { value: 'delayed',  label: 'Delayed' },
]

export default function ProjectModal({ project, prefill = null, subtasks = [], callouts = [], aircraft = [], userProfile, onClose, onSaved, onScheduleStatusChanged, onSubtasksUpdated, technicians = [], assignedTechIds = null, onAddProjectTech, onRemoveProjectTech }) {
  const { profile } = useAuth()
  const orgLocations = profile?.orgs?.settings?.locations ?? []
  const orgDepts = profile?.orgs?.settings?.departments
    ?? Object.keys(DEPT_COLORS).map(k => ({ key: k, label: k, hex: DEPT_COLORS[k].hex }))
  const deptMap = Object.fromEntries(orgDepts.map(d => [d.key, d]))

  const [form, setForm] = useState({
    aircraft_id: '',
    work_order: '',
    work_orders_extra: [],
    customer_name: '',
    arrival_date: '',
    departure_date: '',
    workscope: '',
    departments: [orgDepts[0]?.key ?? 'MX'],
    status: 'Scheduled',
    schedule_status: 'tracking',
    hangar_location: '',
    secondary_registration: '',
    firm_schedule: false,
  })
  const [saving, setSaving] = useState(false)
  const [aircraftSearch, setAircraftSearch] = useState('')
  const [showAircraftDropdown, setShowAircraftDropdown] = useState(false)
  const [showAddAircraft, setShowAddAircraft] = useState(false)
  const [localAircraft, setLocalAircraft] = useState(aircraft)
  const dropdownRef = useRef(null)

  useEffect(() => { setLocalAircraft(aircraft) }, [aircraft])

  useEffect(() => {
    if (project) {
      setForm({
        aircraft_id: project.aircraft_id || '',
        work_order: project.work_order || '',
        work_orders_extra: project.work_orders_extra || [],
        customer_name: project.customer_name || '',
        arrival_date: project.arrival_date || '',
        departure_date: project.departure_date || '',
        workscope: project.workscope || '',
        departments: project.departments || ['MX'],
        status: project.status || 'Scheduled',
        schedule_status: project.schedule_status || 'tracking',
        hangar_location: project.hangar_location || '',
        secondary_registration: project.secondary_registration || '',
        firm_schedule: project.firm_schedule ?? false,
      })
      if (project.aircraft) {
        setAircraftSearch(project.aircraft.registration)
      }
    }
  }, [project])

  // Pre-fill from EBIS push result when creating a new project
  useEffect(() => {
    if (project || !prefill) return
    const reg = (prefill.registration || '').toUpperCase()
    const match = localAircraft.find(a => (a.registration || '').toUpperCase() === reg)
    setForm(f => ({
      ...f,
      work_order: prefill.workOrderNumber || '',
      customer_name: prefill.customer || '',
      aircraft_id: match?.id || '',
    }))
    setAircraftSearch(reg)
  }, [prefill])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowAircraftDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const filteredAircraft = localAircraft.filter(a =>
    a.registration.toLowerCase().includes(aircraftSearch.toLowerCase()) ||
    (a.model || '').toLowerCase().includes(aircraftSearch.toLowerCase()) ||
    (a.manufacturer || '').toLowerCase().includes(aircraftSearch.toLowerCase())
  )

  const selectedAircraft = localAircraft.find(a => a.id === form.aircraft_id)

  const handleAircraftSelect = (a) => {
    set('aircraft_id', a.id)
    setAircraftSearch(a.registration)
    setShowAircraftDropdown(false)
    if (a.customer) set('customer_name', a.customer)
  }

  const handleAircraftAdded = async () => {
    const { data } = await fetchAircraftLatest()
    if (data) {
      setLocalAircraft(data)
      const newest = data[0]
      if (newest) {
        set('aircraft_id', newest.id)
        setAircraftSearch(newest.registration)
      }
    }
    setShowAddAircraft(false)
  }

  const save = async () => {
    if (!form.aircraft_id || !form.arrival_date) return
    setSaving(true)
    try {
      const reg = localAircraft.find(a => a.id === form.aircraft_id)?.registration
      const stamped = { ...form, workflow_as_of: new Date().toISOString() }
      if (project) {
        await updateProject(project.id, stamped)

        // Cascade Complete status to all subtasks when a project is marked Complete
        if (form.status === 'Complete' && project.status !== 'Complete') {
          await completeAllProjectSubtasks(project.id)
        }

        await logChange({
          projectId: project.id, entityType: 'project', entityId: project.id,
          entityLabel: reg || project.aircraft?.registration,
          action: 'updated', oldData: project, newData: form,
          userProfile, aircraft: localAircraft,
        })

        // Shift subtask dates when arrival and/or departure change.
        // Non-RTS subtasks follow arrival. RTS (and INCOMING/RTS) follows departure
        // so it stays anchored to the departure date, preserving custom durations.
        const oldArrival    = project.arrival_date
        const newArrival    = form.arrival_date
        const oldDeparture  = project.departure_date
        const newDeparture  = form.departure_date
        const arrivalChanged   = oldArrival   && newArrival   && oldArrival   !== newArrival
        const departureChanged = oldDeparture && newDeparture && oldDeparture !== newDeparture

        if (arrivalChanged || departureChanged) {
          const arrivalDelta   = arrivalChanged
            ? businessDayDelta(nextBusinessDay(oldArrival),   nextBusinessDay(newArrival))
            : 0
          const departureDelta = departureChanged
            ? businessDayDelta(nextBusinessDay(oldDeparture), nextBusinessDay(newDeparture))
            : 0

          const isRTS = s => s.task_name === 'RTS' || s.task_name === 'INCOMING / RTS'
          const projectSubtasks = subtasks.filter(s => s.project_id === project.id && s.start_date)
          const projectCallouts = callouts.filter(c => c.project_id === project.id && c.date)

          const subtaskShifts = projectSubtasks
            .map(s => {
              const delta = isRTS(s) ? departureDelta : arrivalDelta
              if (delta === 0) return null
              return {
                s,
                newStart: addBusinessDays(s.start_date, delta),
                newEnd:   s.end_date ? addBusinessDays(s.end_date, delta) : s.end_date,
              }
            })
            .filter(Boolean)

          await Promise.all([
            ...subtaskShifts.map(({ s, newStart, newEnd }) =>
              updateSubtask(s.id, { start_date: newStart, end_date: newEnd })
            ),
            ...(arrivalDelta !== 0 ? projectCallouts.map(c => updateCallout(c.id, {
              date:     addBusinessDays(c.date, arrivalDelta),
              end_date: c.end_date ? addBusinessDays(c.end_date, arrivalDelta) : c.end_date,
            })) : []),
          ])

          await Promise.all(
            subtaskShifts.map(({ s, newStart, newEnd }) => logChange({
              projectId: project.id,
              entityType: 'subtask',
              entityId: s.id,
              entityLabel: s.task_name,
              action: 'updated',
              oldData: { start_date: s.start_date, end_date: s.end_date },
              newData: { start_date: newStart, end_date: newEnd },
              userProfile,
            }))
          )
        }
      } else {
        const { data: created } = await createProject(stamped)
        if (created) {
          await logChange({
            projectId: created.id, entityType: 'project', entityId: created.id,
            entityLabel: reg, action: 'created', userProfile,
          })

          // Auto-seed milestones configured in org settings → Settings → Workflows
          const autoTemplates = profile?.orgs?.settings?.autoMilestones ?? []
          if (autoTemplates.length > 0 && form.arrival_date && form.departure_date) {
            const autoTasks = generateAutoSubtasks(autoTemplates, created.id, form.departments ?? [], form.arrival_date, form.departure_date)
            if (autoTasks.length > 0) await createSubtasks(autoTasks)
          }
        }
      }
      // Sync hangar_location to hangar_stack so the aircraft appears on the stack page
      if (form.hangar_location) {
        const rawTail = (reg || form.secondary_registration || '').trim().toUpperCase()
        const tail = rawTail.startsWith('N') ? rawTail.slice(1) : rawTail
        if (tail) {
          const today = new Date().toISOString().slice(0, 10)
          await supabase.from('hangar_stack').upsert(
            { tail, location: form.hangar_location.trim().toUpperCase(), sync_date: today, synced_at: new Date().toISOString(), org_id: userProfile?.org_id },
            { onConflict: 'tail,sync_date' }
          )
        }
      }

      onSaved()
      onClose()
      if (onScheduleStatusChanged && project) {
        const origStatus = project.schedule_status || 'tracking'
        if (form.schedule_status !== origStatus) {
          onScheduleStatusChanged(project.id, origStatus, form.schedule_status)
        }
      }
    } catch (err) {
      console.error('Save error:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
            <h2 className="text-base font-semibold text-gray-900">
              {project ? 'Edit Project' : 'New Project'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">

            {/* Aircraft searchable dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Aircraft *</label>
              <div className="relative" ref={dropdownRef}>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search registration, model..."
                      value={aircraftSearch}
                      onChange={e => {
                        setAircraftSearch(e.target.value)
                        setShowAircraftDropdown(true)
                        if (!e.target.value) set('aircraft_id', '')
                      }}
                      onFocus={() => setShowAircraftDropdown(true)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAddAircraft(true)}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-brand-red border border-brand-red/20 rounded-lg hover:bg-brand-red/10 transition-colors flex-shrink-0"
                    title="Add new aircraft"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New
                  </button>
                </div>

                {/* Selected aircraft display */}
                {selectedAircraft && (
                  <div className="mt-1 flex items-center gap-2 px-2 py-1 bg-brand-red/10 rounded-lg border border-brand-red/10">
                    <svg className="w-3.5 h-3.5 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs font-semibold text-brand-red">{selectedAircraft.registration}</span>
                    {selectedAircraft.model && (
                      <span className="text-xs text-brand-red/70">{selectedAircraft.model}</span>
                    )}
                    {selectedAircraft.aircraft_type && (
                      <span className="text-xs text-brand-red/50">· {selectedAircraft.aircraft_type}</span>
                    )}
                  </div>
                )}

                {/* Dropdown */}
                {showAircraftDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                    {filteredAircraft.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-gray-400 text-center">
                        No aircraft found —{' '}
                        <button
                          type="button"
                          className="text-brand-red underline"
                          onClick={() => {
                            setShowAddAircraft(true)
                            setShowAircraftDropdown(false)
                          }}
                        >
                          add one
                        </button>
                      </div>
                    ) : (
                      filteredAircraft.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => handleAircraftSelect(a)}
                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                            form.aircraft_id === a.id ? 'bg-brand-red/10' : ''
                          }`}
                        >
                          <div>
                            <span className="text-sm font-semibold text-gray-800">{a.registration}</span>
                            {a.model && (
                              <span className="text-xs text-gray-400 ml-2">{a.model}</span>
                            )}
                          </div>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            a.aircraft_type === 'Piston'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-brand-red/10 text-brand-red'
                          }`}>
                            {a.aircraft_type || 'Jet'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {localAircraft.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No aircraft in registry yet. Click "New" to add one.
                </p>
              )}
            </div>

            {/* Alt Registration (for aircraft that change reg while in shop) */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Alt. Registration <span className="text-gray-400 font-normal">(if registration changed while in shop)</span></label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                placeholder="e.g. N12345"
                value={form.secondary_registration || ''}
                onChange={e => set('secondary_registration', e.target.value.toUpperCase())}
              />
            </div>

            {/* Work Order + Customer */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Work Order #</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  placeholder="e.g. WO-2024-001"
                  value={form.work_order || ''}
                  onChange={e => set('work_order', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Customer Name</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  placeholder="e.g. Delta Air Lines"
                  value={form.customer_name || ''}
                  onChange={e => set('customer_name', e.target.value)}
                />
              </div>
            </div>

            {/* Additional Work Orders */}
            {((form.work_orders_extra || []).length > 0 || form.work_order) && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Additional Work Orders</label>
                <div className="space-y-1.5">
                  {(form.work_orders_extra || []).map((wo, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                        placeholder="e.g. WO-2024-002"
                        value={wo}
                        onChange={e => {
                          const next = [...(form.work_orders_extra || [])]
                          next[i] = e.target.value
                          set('work_orders_extra', next)
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => set('work_orders_extra', (form.work_orders_extra || []).filter((_, j) => j !== i))}
                        className="flex-none p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => set('work_orders_extra', [...(form.work_orders_extra || []), ''])}
                    className="flex items-center gap-1 text-xs text-brand-red hover:text-brand-red-dark py-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add work order
                  </button>
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Arrival Date *</label>
                <input
                  type="date"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  value={form.arrival_date}
                  onChange={e => set('arrival_date', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Departure Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  value={form.departure_date || ''}
                  onChange={e => set('departure_date', e.target.value)}
                />
              </div>
            </div>

            {/* Firm Schedule */}
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => set('firm_schedule', !form.firm_schedule)}
                className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                  form.firm_schedule ? 'bg-red-500 border-red-500' : 'border-gray-300 bg-white'
                }`}
              >
                {form.firm_schedule && (
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              <label
                className="text-sm font-medium text-gray-700 cursor-pointer select-none"
                onClick={() => set('firm_schedule', !form.firm_schedule)}
              >
                Firm Schedule?
              </label>
            </div>

            {/* Workscope */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Workscope</label>
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none"
                rows={2}
                placeholder="Describe the work..."
                value={form.workscope || ''}
                onChange={e => set('workscope', e.target.value)}
              />
            </div>

            {/* Departments */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Departments</label>
              <div className="flex flex-wrap gap-1.5">
                {orgDepts.map(({ key: d, hex: dHex }) => {
                  const selected = form.departments.includes(d)
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => {
                        if (selected) {
                          if (form.departments.length > 1) set('departments', form.departments.filter(x => x !== d))
                        } else {
                          set('departments', [...form.departments, d])
                        }
                      }}
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                        selected
                          ? 'border-transparent'
                          : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                      }`}
                      style={selected ? { backgroundColor: dHex + '20', color: dHex } : {}}
                    >
                      {selected && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dHex }} />}
                      {d}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  value={form.status}
                  onChange={e => set('status', e.target.value)}
                >
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Risk Level</label>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  value={form.schedule_status}
                  onChange={e => set('schedule_status', e.target.value)}
                >
                  {SCHEDULE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Location
              </label>
              {orgLocations.length > 0 ? (
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 bg-white"
                  value={form.hangar_location || ''}
                  onChange={e => set('hangar_location', e.target.value)}
                >
                  <option value="">— none —</option>
                  {orgLocations.map(loc => (
                    <option key={loc.key} value={loc.key}>{loc.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  placeholder="e.g. C7, TD, 4"
                  value={form.hangar_location || ''}
                  onChange={e => set('hangar_location', e.target.value.toUpperCase())}
                />
              )}
            </div>

            {/* Bulk milestone push — edit mode only */}
            {project && (
              <BulkMilestonePushPanel
                project={project}
                projectSubtasks={subtasks.filter(s => s.project_id === project.id)}
                userProfile={userProfile}
                onApplied={onSubtasksUpdated}
              />
            )}

            {/* Technicians — edit mode only */}
            {project && onAddProjectTech && onRemoveProjectTech && technicians.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Technicians</label>
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-52 overflow-y-auto">
                  {orgDepts.map(({ key: dept, hex: deptHex }) => {
                    const deptTechs = technicians.filter(t => t.department === dept)
                    if (deptTechs.length === 0) return null
                    return (
                      <div key={dept}>
                        <div
                          className="px-3 py-1 text-xs font-semibold"
                          style={{ backgroundColor: deptHex + '20', color: deptHex }}
                        >{dept}</div>
                        {deptTechs.map(tech => {
                          const assigned = assignedTechIds?.has(tech.id) ?? false
                          return (
                            <button
                              key={tech.id}
                              type="button"
                              onClick={() => assigned
                                ? onRemoveProjectTech(project.id, tech.id)
                                : onAddProjectTech(project.id, tech.id)
                              }
                              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors border-t border-gray-50 text-left"
                            >
                              <div
                                className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors"
                                style={assigned ? { backgroundColor: deptHex + '20', color: deptHex, borderColor: deptHex } : { borderColor: '#d1d5db' }}
                              >
                                {assigned && (
                                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <span className="text-sm text-gray-800">{tech.first_name} {tech.last_name}</span>
                            </button>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || !form.aircraft_id || !form.arrival_date}
              className="text-sm font-medium bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white px-5 py-2 rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : project ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </div>
      </div>

      {/* Inline Add Aircraft modal */}
      {showAddAircraft && (
        <AircraftModal
          aircraft={null}
          onClose={() => setShowAddAircraft(false)}
          onSaved={handleAircraftAdded}
          existingAircraft={localAircraft}
        />
      )}
    </>
  )
}